import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from '../utils/Seo';
import { auth, db } from '../firebase-config';
import { signOut } from 'firebase/auth';
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc, onSnapshot } from 'firebase/firestore';
import { useNavigate, Routes, Route } from 'react-router-dom';

// Import sub-components
import AdminNav from './admin/AdminNav';
import AdminOverview from './admin/AdminOverview';
import AdminOffers from './admin/AdminOffers';
import AdminPackages from './admin/AdminPackages';
import AdminExtraOffers from './admin/AdminExtraOffers';
import AdminSettings from './admin/AdminSettings';
import AdminSectionSettings from './admin/AdminSectionSettings';
import AdminMessages from './admin/AdminMessages';
import AdminCalendar from './admin/AdminCalendar';
import Films from './admin/Films';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [extraOffers, setExtraOffers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [settings, setSettings] = useState({
    email: '',
    phone: '',
    instagram: '',
    youtube: '',
    viber: '',
    whatsapp: '',
    address: '',
    seoTitle: '',
    seoDescription: '',
    seoKeywords: '',
    telegramBotToken: '',
    telegramChatId: ''
  });
  
  // Translation & Selection states
  const [currentServiceBs, setCurrentServiceBs] = useState("");
  const [currentServiceEn, setCurrentServiceEn] = useState("");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Form states
  const [newOffer, setNewOffer] = useState({
    name: '',
    datum: '',
    nazivPaketa: '',
    opis: { bs: '', en: '' },
    cijena: '',
    valuta: 'BAM',
    status: 'draft',
    prikaziNaNaslovnoj: false,
    id: '', 
    slikaDesktop: 'hero1d.jpg',
    slikaMobile: 'hero1m.jpg',
    email: '',
    phone: '',
    message: '',
    usluge: [],
    extraOffers: [],
    displayLanguage: 'bs',
    privacyOption: { enabled: false, price: '150', valuta: 'BAM' } // Added this
  });

  const [newPackage, setNewPackage] = useState({
    naziv: { bs: '', en: '' },
    opis: { bs: '', en: '' },
    cijena: '',
    valuta: 'BAM',
    usluge: [],
    slika: '',
    prikaziNaNaslovnoj: false
  });

  const [newExtraOffer, setNewExtraOffer] = useState({
    naziv: { bs: '', en: '' },
    cijena: '',
    valuta: 'BAM'
  });

  const [editingId, setEditingId] = useState(null);
  const [editingPackageId, setEditingPackageId] = useState(null);
  const [editingExtraOfferId, setEditingExtraOfferId] = useState(null);

  const getPackages = useCallback(async () => {
    try {
      const data = await getDocs(collection(db, "packages"));
      setPackages(data.docs.map((doc) => ({ ...doc.data(), firestoreId: doc.id })));
    } catch (error) {
      console.error("Error fetching packages:", error);
    }
  }, []);

  const getExtraOffers = useCallback(async () => {
    try {
      const data = await getDocs(collection(db, "extraOffers"));
      setExtraOffers(data.docs.map((doc) => ({ ...doc.data(), firestoreId: doc.id })));
    } catch (error) {
      console.error("Error fetching extra offers:", error);
    }
  }, []);

  const getMessages = useCallback(async () => {
    try {
      const data = await getDocs(collection(db, "messages"));
      setMessages(data.docs.map((doc) => ({ ...doc.data(), firestoreId: doc.id })));
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  }, []);

  const getSettings = useCallback(async () => {
    try {
      const data = await getDocs(collection(db, "settings"));
      if (data.docs.length > 0) {
        setSettings({ ...data.docs[0].data(), firestoreId: data.docs[0].id });
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  }, []);

  useEffect(() => {
    // Real-time listener for offers
    const unsubscribeOffers = onSnapshot(collection(db, "offers"), (snapshot) => {
      setOffers(snapshot.docs.map((doc) => ({ ...doc.data(), firestoreId: doc.id })));
    }, (error) => {
      console.error("Error listening to offers:", error);
    });

    const loadData = async () => {
      await Promise.all([
        getPackages(),
        getExtraOffers(),
        getMessages(),
        getSettings()
      ]);
      setLoading(false);
    };
    loadData();

    return () => unsubscribeOffers();
  }, [getPackages, getExtraOffers, getMessages, getSettings]);

  // --- HANDLERS ---
  const handleLogout = async () => {
    await signOut(auth);
    navigate('/login');
  };

  const handleOfferInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.includes('.')) {
        const [field, lang] = name.split('.');
        setNewOffer(prev => ({ ...prev, [field]: { ...prev[field], [lang]: value } }));
    } else {
        setNewOffer(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }
  };

  const handlePackageInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.includes('.')) {
        const [field, lang] = name.split('.');
        setNewPackage(prev => ({ ...prev, [field]: { ...prev[field], [lang]: value } }));
    } else {
        setNewPackage(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }
  };

  const handleExtraOfferInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
        const [field, lang] = name.split('.');
        setNewExtraOffer(prev => ({ ...prev, [field]: { ...prev[field], [lang]: value } }));
    } else {
        setNewExtraOffer(prev => ({ ...prev, [name]: value }));
    }
  };

  const addServiceToPackage = (e) => {
    const isEnter = e.key === 'Enter';
    const isClick = !e.key;
    if ((isEnter || isClick) && currentServiceBs.trim() !== "") {
      e.preventDefault();
      setNewPackage(prev => ({
        ...prev,
        usluge: [...(prev.usluge || []), { bs: currentServiceBs.trim(), en: currentServiceEn.trim() }]
      }));
      setCurrentServiceBs("");
      setCurrentServiceEn("");
    }
  };

  const removeServiceFromPackage = (index) => {
    setNewPackage(prev => ({ ...prev, usluge: prev.usluge.filter((_, i) => i !== index) }));
  };

  const addServiceToOffer = (e) => {
    const isEnter = e.key === 'Enter';
    const isClick = !e.key;
    if ((isEnter || isClick) && currentServiceBs.trim() !== "") {
      e.preventDefault();
      setNewOffer(prev => ({
        ...prev,
        usluge: [...(prev.usluge || []), { bs: currentServiceBs.trim(), en: currentServiceEn.trim() }]
      }));
      setCurrentServiceBs("");
      setCurrentServiceEn("");
    }
  };

  const removeServiceFromOffer = (index) => {
    setNewOffer(prev => ({ ...prev, usluge: prev.usluge.filter((_, i) => i !== index) }));
  };

  const applyPackageToOffer = (pkg) => {
    setNewOffer(prev => ({
      ...prev,
      nazivPaketa: typeof pkg.naziv === 'object' ? pkg.naziv.bs : pkg.naziv,
      opis: pkg.opis && typeof pkg.opis === 'object' ? { ...pkg.opis } : { bs: pkg.opis || '', en: '' },
      trajanje: pkg.trajanje && typeof pkg.trajanje === 'object' ? { ...pkg.trajanje } : { bs: pkg.trajanje || '', en: '' },
      cijena: pkg.cijena,
      valuta: pkg.valuta || 'BAM',
      slikaDesktop: pkg.slika || 'hero1d.jpg',
      slikaMobile: pkg.slika || (pkg.slika || 'hero1d.jpg'),
      usluge: pkg.usluge && Array.isArray(pkg.usluge) ? [...pkg.usluge] : [],
      // Reset or copy these if we decide to add them to packages
      extraOffers: pkg.extraOffers || [], 
      privacyOption: pkg.privacyOption || { enabled: true, price: '150', valuta: 'BAM' }
    }));
  };

  const handleOfferSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        const offerDoc = doc(db, "offers", editingId);
        const { firestoreId, ...dataToUpdate } = newOffer; 
        await updateDoc(offerDoc, dataToUpdate);
        setEditingId(null);
      } else {
        await addDoc(collection(db, "offers"), { ...newOffer, createdAt: new Date() });
      }
      setNewOffer({
        name: '', datum: '', nazivPaketa: '', opis: { bs: '', en: '' }, cijena: '', valuta: 'BAM', status: 'draft', prikaziNaNaslovnoj: false, id: '', slikaDesktop: 'hero1d.jpg', slikaMobile: 'hero1d.jpg', email: '', phone: '', message: '', usluge: [], extraOffers: [],
        displayLanguage: 'bs',
        privacyOption: { enabled: false, price: '150', valuta: 'BAM' } // Reset state
      });
      alert("Ponuda uspješno sačuvana!");
    } catch (error) { console.error(error); alert("Greška!"); }
  };

  const handlePackageSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPackageId) {
        const packageDoc = doc(db, "packages", editingPackageId);
        const { firestoreId, ...dataToUpdate } = newPackage; 
        await updateDoc(packageDoc, dataToUpdate);
        setEditingPackageId(null);
      } else {
        await addDoc(collection(db, "packages"), { ...newPackage, createdAt: new Date() });
      }
      setNewPackage({
        naziv: { bs: '', en: '' }, opis: { bs: '', en: '' }, cijena: '', valuta: 'BAM', usluge: [], slika: '', prikaziNaNaslovnoj: false
      });
      getPackages();
      alert("Paket uspješno sačuvan!");
    } catch (error) { console.error(error); alert("Greška!"); }
  };

  const handleExtraOfferSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingExtraOfferId) {
        const docRef = doc(db, "extraOffers", editingExtraOfferId);
        const { firestoreId, ...dataToUpdate } = newExtraOffer;
        await updateDoc(docRef, dataToUpdate);
        setEditingExtraOfferId(null);
      } else {
        await addDoc(collection(db, "extraOffers"), { ...newExtraOffer, createdAt: new Date() });
      }
      setNewExtraOffer({ naziv: { bs: '', en: '' }, cijena: '', valuta: 'BAM' });
      getExtraOffers();
      alert("Dodatna ponuda sačuvana!");
    } catch (error) { console.error(error); }
  };

  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    try {
      if (settings.firestoreId) {
        const { firestoreId, ...dataToUpdate } = settings;
        const settingsDoc = doc(db, "settings", firestoreId);
        await updateDoc(settingsDoc, dataToUpdate);
      } else {
        await addDoc(collection(db, "settings"), settings);
      }
      alert("Postavke su spasene!");
      getSettings();
    } catch (error) {
      console.error("Error saving settings:", error);
      alert("Greška pri spašavanju postavki.");
    }
  };

  const deleteOffer = async (id) => {
    if (window.confirm("Obriši?")) {
      await deleteDoc(doc(db, "offers", id));
    }
  };

  const deletePackage = async (id) => {
    if (window.confirm("Obriši?")) {
      await deleteDoc(doc(db, "packages", id));
      getPackages();
    }
  };

  const deleteExtraOffer = async (id) => {
    if (window.confirm("Obriši?")) {
      await deleteDoc(doc(db, "extraOffers", id));
      getExtraOffers();
    }
  };

  const editOffer = useCallback((offer) => {
    setEditingId(offer.firestoreId);
    setNewOffer({ 
      ...offer, 
      usluge: offer.usluge || [], 
      opis: offer.opis || { bs: '', en: '' },
      extraOffers: offer.extraOffers || [],
      privacyOption: offer.privacyOption || { enabled: false, price: '150', valuta: 'BAM' } 
    });
    navigate(`/admin/offers/edit/${offer.firestoreId}`);
  }, [navigate]);

  const editPackage = useCallback((pkg) => {
    setEditingPackageId(pkg.firestoreId);
    setNewPackage({
      ...pkg,
      naziv: pkg.naziv || { bs: '', en: '' },
      opis: pkg.opis || { bs: '', en: '' },
      usluge: pkg.usluge || []
    });
    navigate(`/admin/packages/edit/${pkg.firestoreId}`);
  }, [navigate]);

  const editExtraOffer = useCallback((item) => {
    setEditingExtraOfferId(item.firestoreId);
    setNewExtraOffer({
      ...item,
      naziv: item.naziv || { bs: '', en: '' }
    });
    navigate(`/admin/extra-offers/edit/${item.firestoreId}`);
  }, [navigate]);

  return (
    <>
      <Helmet>
        <title>Admin Dashboard - Nejira Zukić Weddings</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="flex h-screen bg-gray-50 font-sans text-gray-900 overflow-hidden">
      <AdminNav 
        offersCount={offers.length} 
        packagesCount={packages.length}
        messagesCount={messages.filter(m => m.status === 'new').length}
        isOpen={isMobileMenuOpen}
        setIsOpen={setIsMobileMenuOpen}
        handleLogout={handleLogout}
      />
      
      <main className="flex-1 overflow-y-auto overflow-x-hidden p-6 lg:p-10">
        <div className="max-w-7xl mx-auto">
          {/* Mobile header with menu toggle */}
          <div className="lg:hidden flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsMobileMenuOpen(prev => !prev)}
                className="p-2 bg-white border rounded-md shadow-sm"
                aria-label="Toggle menu"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 6h18M3 12h18M3 18h18" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <div>
                <div className="text-sm font-bold">Admin Panel</div>
                <div className="text-[10px] text-gray-500">Nejira Zukić</div>
              </div>
            </div>
            <div className="text-right">
              <button onClick={handleLogout} className="text-xs text-red-600 font-bold">Odjavi se</button>
            </div>
          </div>
          <Routes>
            <Route path="/" element={<AdminOverview offers={offers} packages={packages} />} />

            <Route path="/messages" element={<AdminMessages />} />
            <Route path="/messages/view/:id" element={<AdminMessages />} />

            <Route path="/calendar" element={<AdminCalendar offers={offers} />} />

            <Route path="/offers/*" element={
              <AdminOffers 
                packages={packages}
                extraOffers={extraOffers}
                newOffer={newOffer}
                setNewOffer={setNewOffer}
                handleOfferSubmit={handleOfferSubmit}
                handleOfferInputChange={handleOfferInputChange}
                applyPackageToOffer={applyPackageToOffer}
                currentServiceBs={currentServiceBs}
                setCurrentServiceBs={setCurrentServiceBs}
                currentServiceEn={currentServiceEn}
                setCurrentServiceEn={setCurrentServiceEn}
                addServiceToOffer={addServiceToOffer}
                removeServiceFromOffer={removeServiceFromOffer}
                offers={offers}
                editOffer={editOffer}
                deleteOffer={deleteOffer}
                loading={loading}
                setEditingId={setEditingId}
              />
            } />

            <Route path="/packages/*" element={
              <AdminPackages 
                editingPackageId={editingPackageId}
                handlePackageSubmit={handlePackageSubmit}
                newPackage={newPackage}
                setNewPackage={setNewPackage}
                handlePackageInputChange={handlePackageInputChange}
                currentServiceBs={currentServiceBs}
                setCurrentServiceBs={setCurrentServiceBs}
                currentServiceEn={currentServiceEn}
                setCurrentServiceEn={setCurrentServiceEn}
                addServiceToPackage={addServiceToPackage}
                removeServiceFromPackage={removeServiceFromPackage}
                setEditingPackageId={setEditingPackageId}
                loading={loading}
                packages={packages}
                editPackage={editPackage}
                deletePackage={deletePackage}
              />
            } />

            <Route path="/extra-offers/*" element={
              <AdminExtraOffers 
                extraOffers={extraOffers}
                newExtraOffer={newExtraOffer}
                setNewExtraOffer={setNewExtraOffer}
                handleExtraOfferSubmit={handleExtraOfferSubmit}
                handleExtraOfferInputChange={handleExtraOfferInputChange}
                editingExtraOfferId={editingExtraOfferId}
                setEditingExtraOfferId={setEditingExtraOfferId}
                deleteExtraOffer={deleteExtraOffer}
                editExtraOffer={editExtraOffer}
                loading={loading}
              />
            } />

            <Route path="/settings" element={
              <AdminSettings 
                settings={settings}
                setSettings={setSettings}
                handleSettingsSubmit={handleSettingsSubmit}
                loading={loading}
              />
            } />

            <Route path="/films" element={
              <Films />
            } />

            <Route path="/sections/*" element={
              <AdminSectionSettings 
                settings={settings}
                setSettings={setSettings}
                handleSettingsSubmit={handleSettingsSubmit}
                loading={loading}
              />
            } />
          </Routes>
        </div>
      </main>
    </div>
    </>
  );
};

export default AdminDashboard;

